import { useState, useEffect, useMemo } from 'react';
import { Button, Input, Card, useToast } from '../components/common';
import api, { API_BASE_URL } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { ApiKey } from '../types';

interface ApiKeyWithUser extends ApiKey {
  user_id: number;
}

interface JinaParams {
  url: string;
  engine: 'default' | 'browser' | 'direct';
  format: 'markdown' | 'html' | 'text' | 'screenshot' | 'pageshot';
  json_response: boolean;
  timeout: number;
  token_budget: string;
  target_selector: string;
  wait_for_selector: string;
  exclude_selector: string;
  remove_all_images: boolean;
  links_summary: 'none' | 'all' | 'relevant';
  images_summary: 'none' | 'all' | 'relevant';
  bypass_cache: boolean;
  cache_tolerance: string;
  stream_mode: boolean;
  github_flavored_markdown: boolean;
  openai_citation_format: boolean;
  viewport_width: string;
  viewport_height: string;
  forward_cookies: string;
  image_caption: boolean;
  use_proxy: boolean;
  proxy_country: string;
  page_ready_timing: 'networkIdle' | 'domContentLoaded' | 'load';
  custom_user_agent: string;
  custom_referer: string;
  retain_base64_images: boolean;
  no_cache_no_track: boolean;
  browser_locale: string;
  respect_robots_txt: string;
  include_iframe: boolean;
  include_shadow_dom: boolean;
  use_final_url_as_base: boolean;
  heading_style: 'atx' | 'setext';
  hr_style: '***' | '---' | '___';
  bullet_style: '-' | '*' | '+';
  emphasis_style: '_' | '*';
  strong_style: '__' | '**';
  link_style: 'inlined' | 'referenced';
}

const defaultParams: JinaParams = {
  url: '', engine: 'default', format: 'markdown', json_response: true, timeout: 30,
  token_budget: '', target_selector: '', wait_for_selector: '', exclude_selector: '',
  remove_all_images: false, links_summary: 'none', images_summary: 'none',
  bypass_cache: true, cache_tolerance: '', stream_mode: false, github_flavored_markdown: true,
  openai_citation_format: false, viewport_width: '', viewport_height: '',
  forward_cookies: '', image_caption: false, use_proxy: false, proxy_country: '',
  page_ready_timing: 'networkIdle', custom_user_agent: '', custom_referer: '',
  retain_base64_images: false, no_cache_no_track: true, browser_locale: '',
  respect_robots_txt: '', include_iframe: false, include_shadow_dom: false,
  use_final_url_as_base: false, heading_style: 'atx', hr_style: '***', bullet_style: '-',
  emphasis_style: '_', strong_style: '**', link_style: 'inlined',
};

export default function PlaygroundPage() {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [params, setParams] = useState<JinaParams>(defaultParams);
  const [apiKey, setApiKey] = useState<string>('');
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [response, setResponse] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showMarkdownOptions, setShowMarkdownOptions] = useState(false);

  useEffect(() => {
    api.get<ApiKeyWithUser[]>('/api/v1/api-keys').then(res => {
      // Only show current user's own keys (filter by user_id)
      const ownKeys = res.data.filter(k => k.is_active && k.user_id === user?.id);
      setApiKeys(ownKeys);
      if (ownKeys.length > 0) setApiKey(ownKeys[0].key_value);
    }).catch(() => {});
  }, [user?.id]);

  const buildRequestBody = () => {
    const body: Record<string, unknown> = { url: params.url };
    if (params.engine !== 'default') body.engine = params.engine;
    if (params.format !== 'markdown') body.format = params.format;
    if (params.json_response) body.json_response = true;
    if (params.timeout !== 30) body.timeout = params.timeout;
    if (params.token_budget) body.token_budget = parseInt(params.token_budget);
    if (params.target_selector) body.target_selector = params.target_selector;
    if (params.wait_for_selector) body.wait_for_selector = params.wait_for_selector;
    if (params.exclude_selector) body.exclude_selector = params.exclude_selector;
    if (params.remove_all_images) body.remove_all_images = true;
    if (params.links_summary !== 'none') body.links_summary = params.links_summary;
    if (params.images_summary !== 'none') body.images_summary = params.images_summary;
    if (params.bypass_cache) body.bypass_cache = true;
    if (params.cache_tolerance) body.cache_tolerance = parseInt(params.cache_tolerance);
    if (params.stream_mode) body.stream_mode = true;
    if (!params.github_flavored_markdown) body.github_flavored_markdown = false;
    if (params.openai_citation_format) body.openai_citation_format = true;
    if (params.viewport_width && params.viewport_height) body.viewport = `${params.viewport_width}x${params.viewport_height}`;
    if (params.forward_cookies) body.forward_cookies = params.forward_cookies;
    if (params.image_caption) body.image_caption = true;
    if (params.use_proxy) body.use_proxy = true;
    if (params.proxy_country) body.proxy_country = params.proxy_country;
    if (params.page_ready_timing !== 'networkIdle') body.page_ready_timing = params.page_ready_timing;
    if (params.custom_user_agent) body.custom_user_agent = params.custom_user_agent;
    if (params.custom_referer) body.custom_referer = params.custom_referer;
    if (params.retain_base64_images) body.retain_base64_images = true;
    if (params.no_cache_no_track) body.no_cache_no_track = true;
    if (params.browser_locale) body.browser_locale = params.browser_locale;
    if (params.respect_robots_txt) body.respect_robots_txt = params.respect_robots_txt;
    if (params.include_iframe) body.include_iframe = true;
    if (params.include_shadow_dom) body.include_shadow_dom = true;
    if (params.use_final_url_as_base) body.use_final_url_as_base = true;
    if (params.heading_style !== 'atx') body.heading_style = params.heading_style;
    if (params.hr_style !== '***') body.hr_style = params.hr_style;
    if (params.bullet_style !== '-') body.bullet_style = params.bullet_style;
    if (params.emphasis_style !== '_') body.emphasis_style = params.emphasis_style;
    if (params.strong_style !== '**') body.strong_style = params.strong_style;
    if (params.link_style !== 'inlined') body.link_style = params.link_style;
    return body;
  };

  const curlCommand = useMemo(() => {
    const baseUrl = API_BASE_URL || window.location.origin;
    const body = buildRequestBody();
    return `curl -X POST "${baseUrl}/api/v1/read" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(body)}'`;
  }, [params, apiKey]);

  const handleExecute = async () => {
    if (!params.url) { addToast('error', '請輸入目標網址'); return; }
    try {
      setLoading(true);
      setResponse('');
      const body = buildRequestBody();
      const res = await api.post('/api/v1/read', body, { 
        headers: { 
          'Authorization': `Bearer ${apiKey}`,
          'X-Request-Source': 'playground',
          'Content-Type': 'application/json'
        } 
      });
      setResponse(typeof res.data === 'string' ? res.data : JSON.stringify(res.data, null, 2));
      addToast('success', '請求成功');
    } catch (error: any) {
      setResponse(error.response?.data?.error?.message || error.message || '請求失敗');
      addToast('error', '請求失敗');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    addToast('success', '已複製到剪貼簿');
  };

  const SelectField = ({ label, value, onChange, options, hint }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; hint?: string }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {hint && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
    </div>
  );

  const CheckboxField = ({ label, checked, onChange, hint }: { label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string }) => (
    <label className="flex items-start gap-2 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="rounded mt-0.5" />
      <div>
        <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
        {hint && <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
      </div>
    </label>
  );

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Playground</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">基本設定</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">如果不清楚這些選項，請使用預設值即可</p>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">API Key</label>
                <select value={apiKey} onChange={(e) => setApiKey(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  {apiKeys.map(k => <option key={k.id} value={k.key_value}>{k.description || k.key_value.substring(0, 16) + '...'}</option>)}
                </select>
              </div>
              <Input label="目標網址" value={params.url} onChange={(e) => setParams({ ...params, url: e.target.value })} placeholder="https://example.com" required />
              <div className="grid grid-cols-2 gap-4">
                <SelectField label="讀取引擎" value={params.engine} onChange={(v) => setParams({ ...params, engine: v as any })} options={[{ value: 'default', label: '默認' }, { value: 'browser', label: 'Browser (品質優先)' }, { value: 'direct', label: 'Direct (速度優先)' }]} hint="影響內容質量、速度和完整性" />
                <SelectField label="內容格式" value={params.format} onChange={(v) => setParams({ ...params, format: v as any })} options={[{ value: 'markdown', label: 'Markdown' }, { value: 'html', label: 'HTML' }, { value: 'text', label: '純文字' }, { value: 'screenshot', label: '截圖' }, { value: 'pageshot', label: '整頁截圖' }]} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="超時時間（秒）" type="number" min="1" max="180" value={params.timeout.toString()} onChange={(e) => setParams({ ...params, timeout: parseInt(e.target.value) || 30 })} />
                <Input label="詞元預算限制" type="number" value={params.token_budget} onChange={(e) => setParams({ ...params, token_budget: e.target.value })} placeholder="200000" />
              </div>
              <div className="flex flex-wrap gap-4">
                <CheckboxField label="JSON 回應" checked={true} onChange={() => {}} hint="固定啟用，不可關閉" />
                <CheckboxField label="繞過快取" checked={params.bypass_cache} onChange={(v) => setParams({ ...params, bypass_cache: v })} />
                <CheckboxField label="請勿緩存或追蹤" checked={params.no_cache_no_track} onChange={(v) => setParams({ ...params, no_cache_no_track: v })} hint="僅用於敏感網址" />
              </div>
              <Button onClick={handleExecute} disabled={loading} className="w-full">{loading ? '執行中...' : '執行請求'}</Button>
            </div>
          </Card>

          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">進階選項</h2>
              <button onClick={() => setShowAdvanced(!showAdvanced)} className="text-sm text-primary-600 dark:text-primary-400">{showAdvanced ? '收起' : '展開'}</button>
            </div>
            {showAdvanced && (
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="僅提取 (CSS 選擇器)" value={params.target_selector} onChange={(e) => setParams({ ...params, target_selector: e.target.value })} placeholder="article, .main-content" />
                  <Input label="等待 (CSS 選擇器)" value={params.wait_for_selector} onChange={(e) => setParams({ ...params, wait_for_selector: e.target.value })} placeholder="div.main" />
                  <Input label="排除 (CSS 選擇器)" value={params.exclude_selector} onChange={(e) => setParams({ ...params, exclude_selector: e.target.value })} placeholder="nav, footer, .ads" />
                  <Input label="緩存容錯率（秒）" type="number" value={params.cache_tolerance} onChange={(e) => setParams({ ...params, cache_tolerance: e.target.value })} placeholder="300" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <SelectField label="鏈接摘要" value={params.links_summary} onChange={(v) => setParams({ ...params, links_summary: v as any })} options={[{ value: 'none', label: '無' }, { value: 'all', label: '全部' }, { value: 'relevant', label: '相關' }]} hint="創建按鈕和鏈接部分" />
                  <SelectField label="圖片摘要" value={params.images_summary} onChange={(v) => setParams({ ...params, images_summary: v as any })} options={[{ value: 'none', label: '無' }, { value: 'all', label: '全部' }, { value: 'relevant', label: '相關' }]} hint="創建圖片部分" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input label="視口寬度" type="number" value={params.viewport_width} onChange={(e) => setParams({ ...params, viewport_width: e.target.value })} placeholder="1920" />
                  <Input label="視口高度" type="number" value={params.viewport_height} onChange={(e) => setParams({ ...params, viewport_height: e.target.value })} placeholder="1080" />
                </div>
                <SelectField label="頁面準備時間" value={params.page_ready_timing} onChange={(v) => setParams({ ...params, page_ready_timing: v as any })} options={[{ value: 'networkIdle', label: '網絡空閒' }, { value: 'domContentLoaded', label: 'DOM 載入完成' }, { value: 'load', label: '完全載入' }]} hint="較晚的計時方式等待更長但能捕捉更多動態內容" />
                <div className="flex flex-wrap gap-4">
                  <CheckboxField label="刪除所有圖片" checked={params.remove_all_images} onChange={(v) => setParams({ ...params, remove_all_images: v })} />
                  <CheckboxField label="OpenAI 引用格式" checked={params.openai_citation_format} onChange={(v) => setParams({ ...params, openai_citation_format: v })} />
                  <CheckboxField label="圖片說明" checked={params.image_caption} onChange={(v) => setParams({ ...params, image_caption: v })} hint="為圖片添加標題" />
                  <CheckboxField label="流模式" checked={params.stream_mode} onChange={(v) => setParams({ ...params, stream_mode: v })} />
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">代理與緩存</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <CheckboxField label="使用代理服務器" checked={params.use_proxy} onChange={(v) => setParams({ ...params, use_proxy: v })} />
                    <Input label="代理國家代碼" value={params.proxy_country} onChange={(e) => setParams({ ...params, proxy_country: e.target.value })} placeholder="TW, US, JP" disabled={!params.use_proxy} />
                  </div>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">瀏覽器設定</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="自定義 User Agent" value={params.custom_user_agent} onChange={(e) => setParams({ ...params, custom_user_agent: e.target.value })} placeholder="Mozilla/5.0..." />
                    <Input label="自定義 Referer" value={params.custom_referer} onChange={(e) => setParams({ ...params, custom_referer: e.target.value })} placeholder="https://google.com" />
                    <Input label="瀏覽器區域設置" value={params.browser_locale} onChange={(e) => setParams({ ...params, browser_locale: e.target.value })} placeholder="zh-TW, en-US" />
                    <Input label="轉發 Cookie" value={params.forward_cookies} onChange={(e) => setParams({ ...params, forward_cookies: e.target.value })} placeholder="session=abc123" />
                  </div>
                  <div className="flex flex-wrap gap-4 mt-3">
                    <CheckboxField label="保存 Base64 圖像" checked={params.retain_base64_images} onChange={(v) => setParams({ ...params, retain_base64_images: v })} />
                    <CheckboxField label="包含 iframe 內容" checked={params.include_iframe} onChange={(v) => setParams({ ...params, include_iframe: v })} />
                    <CheckboxField label="包含 Shadow DOM" checked={params.include_shadow_dom} onChange={(v) => setParams({ ...params, include_shadow_dom: v })} />
                    <CheckboxField label="使用最終 URL 作為基準" checked={params.use_final_url_as_base} onChange={(v) => setParams({ ...params, use_final_url_as_base: v })} />
                  </div>
                  <div className="mt-3">
                    <Input label="尊重 robots.txt (機器人名稱)" value={params.respect_robots_txt} onChange={(e) => setParams({ ...params, respect_robots_txt: e.target.value })} placeholder="Googlebot" />
                  </div>
                </div>
              </div>
            )}
          </Card>

          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Markdown 樣式</h2>
              <button onClick={() => setShowMarkdownOptions(!showMarkdownOptions)} className="text-sm text-primary-600 dark:text-primary-400">{showMarkdownOptions ? '收起' : '展開'}</button>
            </div>
            {showMarkdownOptions && (
              <div className="p-4 space-y-4">
                <CheckboxField label="GitHub 風格 Markdown" checked={params.github_flavored_markdown} onChange={(v) => setParams({ ...params, github_flavored_markdown: v })} />
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <SelectField label="標題樣式" value={params.heading_style} onChange={(v) => setParams({ ...params, heading_style: v as any })} options={[{ value: 'atx', label: '# 哈希風格' }, { value: 'setext', label: '下劃線風格' }]} />
                  <SelectField label="水平線樣式" value={params.hr_style} onChange={(v) => setParams({ ...params, hr_style: v as any })} options={[{ value: '***', label: '***' }, { value: '---', label: '---' }, { value: '___', label: '___' }]} />
                  <SelectField label="項目符號樣式" value={params.bullet_style} onChange={(v) => setParams({ ...params, bullet_style: v as any })} options={[{ value: '-', label: '-' }, { value: '*', label: '*' }, { value: '+', label: '+' }]} />
                  <SelectField label="強調風格" value={params.emphasis_style} onChange={(v) => setParams({ ...params, emphasis_style: v as any })} options={[{ value: '_', label: '_斜體_' }, { value: '*', label: '*斜體*' }]} />
                  <SelectField label="加粗風格" value={params.strong_style} onChange={(v) => setParams({ ...params, strong_style: v as any })} options={[{ value: '**', label: '**粗體**' }, { value: '__', label: '__粗體__' }]} />
                  <SelectField label="鏈接樣式" value={params.link_style} onChange={(v) => setParams({ ...params, link_style: v as any })} options={[{ value: 'inlined', label: '內聯' }, { value: 'referenced', label: '引用' }]} />
                </div>
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">cURL 指令</h2>
              <Button size="sm" variant="secondary" onClick={() => copyToClipboard(curlCommand)}>複製</Button>
            </div>
            <div className="p-4">
              <pre className="bg-gray-100 dark:bg-gray-700 p-3 rounded text-sm overflow-auto max-h-40 whitespace-pre-wrap">{curlCommand}</pre>
            </div>
          </Card>
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">回應內容</h2>
              {response && <Button size="sm" variant="secondary" onClick={() => copyToClipboard(response)}>複製</Button>}
            </div>
            <div className="p-4">
              {loading ? (
                <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
              ) : response ? (
                <pre className="bg-gray-100 dark:bg-gray-700 p-3 rounded text-sm overflow-auto max-h-[600px] whitespace-pre-wrap">{response}</pre>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">執行請求後將在此顯示回應</p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
